import React, { useMemo } from 'react';
import { PreviewPanel as StyledPreviewPanel, PreviewContent } from '../../styled';
import { HotelResultsView } from '../HotelResultsView/HotelResultsView';
import { EmptyState } from '../EmptyState/EmptyState';
import { PanelRelativeWrapper } from './PreviewPanel.styles';
import { PreviewPanelProps } from './PreviewPanel.types';

export const PreviewPanel: React.FC<PreviewPanelProps> = ({
  currentView,
  viewData,
  viewMode,
  uiData,
  hoveredHotelId: _hoveredHotelId,
  onViewToggle: _onViewToggle,
  onHotelHover: _onHotelHover,
  onOpenHotelPanel,
  onPropertyMediaLoaded,
  renderEmptyState,
  isStaticMap = false,
  isMapLoading = false,
  miniSearchBarModel,
  isMiniSearchLoading = false,
  llmMetadata,
  onNLSSearchComplete,
}) => {
  const isMapView = currentView?.viewType === 'map' || viewMode === 'map';

  const renderPreviewContent = useMemo(() => {
    switch (currentView.viewType) {
      case 'hotel-list':
        return (
          <HotelResultsView
            viewData={viewData}
            onOpenHotelPanel={onOpenHotelPanel}
            onPropertyMediaLoaded={onPropertyMediaLoaded}
            isStaticMap={isStaticMap}
            isMapLoading={isMapLoading}
            uiData={uiData}
            isMapView={isMapView}
            miniSearchBarModel={miniSearchBarModel}
            isMiniSearchLoading={isMiniSearchLoading}
            llmMetadata={llmMetadata}
            onNLSSearchComplete={onNLSSearchComplete}
          />
        );

      case 'empty':
        return (
          <HotelResultsView
            viewData={null}
            onOpenHotelPanel={onOpenHotelPanel}
            onPropertyMediaLoaded={onPropertyMediaLoaded}
            isStaticMap={true}
            isMapLoading={isMapLoading}
            isMapView={true}
          />
        );

      case 'dining':
        return (
          <EmptyState
            icon="🍽️"
            title="Restaurant Results"
            description={`${viewData?.restaurants?.length || 0} restaurants found`}
          />
        );

      case 'hotel-detail':
        return <EmptyState icon="🏨" title="Hotel Details" description="Hotel details would appear here" />;

      case 'attractions':
        return (
          <EmptyState
            icon="🎯"
            title="Attractions"
            description={`${viewData?.attractions?.length || 0} attractions found`}
          />
        );

      case 'booking':
      case 'booking_management':
        return (
          <EmptyState
            icon="📋"
            title="Booking Management"
            description="Booking details and management options would appear here"
          />
        );

      case 'map':
        return (
          <HotelResultsView
            viewData={viewData}
            onOpenHotelPanel={onOpenHotelPanel}
            onPropertyMediaLoaded={onPropertyMediaLoaded}
            isStaticMap={isStaticMap}
            isMapLoading={isMapLoading}
            uiData={uiData}
            isMapView={true}
            miniSearchBarModel={miniSearchBarModel}
            isMiniSearchLoading={isMiniSearchLoading}
            llmMetadata={llmMetadata}
            onNLSSearchComplete={onNLSSearchComplete}
          />
        );

      default:
        return renderEmptyState();
    }
  }, [
    currentView.viewType,
    viewData,
    uiData,
    onOpenHotelPanel,
    onPropertyMediaLoaded,
    renderEmptyState,
    isStaticMap,
    isMapLoading,
    isMapView,
    viewMode,
    miniSearchBarModel,
    isMiniSearchLoading,
    llmMetadata,
    onNLSSearchComplete,
  ]);

  return (
    <StyledPreviewPanel>
      <PanelRelativeWrapper>
        <PreviewContent>{renderPreviewContent}</PreviewContent>
      </PanelRelativeWrapper>
    </StyledPreviewPanel>
  );
};
